import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	bulkActions: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(0.5, 1),
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.paper,
		gap: theme.spacing(1),
	},
	bulkActionsLeft: {
		display: "flex",
		alignItems: "center",
	},
}));
 
const sortByRecent = (tickets) => {
	return tickets.sort((a, b) => {
		const dateA = new Date(a.updatedAt || a.lastMessageAt || 0);
		const dateB = new Date(b.updatedAt || b.lastMessageAt || 0);
		return dateB - dateA;
	});
};

const reducer = (state, action) => {

	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload;

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);

			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
			} else {
				state.push(ticket);
			}
		});

		return sortByRecent([...state]);
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);

		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return sortByRecent([...state]);
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticket.id);

		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.push(ticket);
		}

		return sortByRecent([...state]);
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const ticket = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticket.id);

		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.push(ticket);
		}

		return sortByRecent([...state]);
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);

		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}

		return sortByRecent([...state]);
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);

		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return sortByRecent([...state]);
	}

	if (action.type === "RESET") {
		return [];
	}
};

const TicketsList = (props) => {
	const {
		status,
		searchParam,
		showAll,
		selectedQueueIds,
		selectedTagId,
		selectedAssignedUserId,
		selectedDateFrom,
		selectedDateTo,
		selectedWhatsappId,
		selectedTransferred,
		selectedUnreadOnly,
		updateCount,
		style,
		onlyMine = false,
	} = props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const [selectedTicketIds, setSelectedTicketIds] = useState([]);
	const [isBulkClosing, setIsBulkClosing] = useState(false);
	const { user } = useContext(AuthContext);
	const isAdmin = user?.profile?.toUpperCase() === "ADMIN";

	const belongsToSelectedQueues = ticket =>
		!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1;

	const belongsToSelectedFilters = ticket => {
		if (selectedTagId) {
			const directTagMatches = String(ticket.tagId || "") === String(selectedTagId);
			const manyTagsMatch = Array.isArray(ticket.tags) && ticket.tags.some(tag => String(tag.id) === String(selectedTagId));
			if (!directTagMatches && !manyTagsMatch) return false;
		}

		if (selectedAssignedUserId === "unassigned" && ticket.userId) return false;
		if (
			selectedAssignedUserId &&
			selectedAssignedUserId !== "unassigned" &&
			String(ticket.userId || "") !== String(selectedAssignedUserId)
		) {
			return false;
		}

		if (selectedWhatsappId && String(ticket.whatsappId || "") !== String(selectedWhatsappId)) {
			return false;
		}

		if (selectedTransferred === "true" && !ticket.isTransferred) return false;
		if (selectedTransferred === "false" && ticket.isTransferred) return false;
		if (selectedUnreadOnly && Number(ticket.unreadMessages || 0) <= 0) return false;

		if (selectedDateFrom || selectedDateTo) {
			const ticketDate = new Date(ticket.createdAt || ticket.updatedAt || 0);
			if (selectedDateFrom) {
				const fromDate = new Date(`${selectedDateFrom}T00:00:00`);
				if (ticketDate < fromDate) return false;
			}
			if (selectedDateTo) {
				const toDate = new Date(`${selectedDateTo}T23:59:59`);
				if (ticketDate > toDate) return false;
			}
		}

		return true;
	};

	const belongsToCurrentView = ticket => {
		if (!belongsToSelectedQueues(ticket)) return false;
		if (!belongsToSelectedFilters(ticket)) return false;
		if (onlyMine) return ticket.userId === user?.id;
		return !ticket.userId || ticket.userId === user?.id || showAll;
	};

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		setSelectedTicketIds([]);
	}, [
		status,
		searchParam,
		dispatch,
		showAll,
		selectedQueueIds,
		selectedTagId,
		selectedAssignedUserId,
		selectedDateFrom,
		selectedDateTo,
		selectedWhatsappId,
		selectedTransferred,
		selectedUnreadOnly,
	]);

	const { tickets, hasMore, loading } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		tagId: selectedTagId || undefined,
		assignedUserId: selectedAssignedUserId || undefined,
		dateFrom: selectedDateFrom || undefined,
		dateTo: selectedDateTo || undefined,
		whatsappId: selectedWhatsappId || undefined,
		transferred: selectedTransferred || undefined,
		withUnreadMessages: selectedUnreadOnly ? "true" : undefined,
	});

	useEffect(() => {
		if (!status && !searchParam) return;

		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = ticket =>
			!searchParam &&
			belongsToCurrentView(ticket);

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		socket.on("connect", () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		});

		socket.on("ticket", data => {
			if (data.action === "updateUnread") {
				dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
			}

			if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
				dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
			}

			if (
				data.action === "update" &&
				(notBelongsToUserQueues(data.ticket) || !belongsToCurrentView(data.ticket))
			) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		});

		socket.on("appMessage", data => {
			if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: data.ticket,
				});
			}
		});

		socket.on("contact", data => {
			if (data.action === "update") {
				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: data.contact,
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [
		status,
		searchParam,
		showAll,
		user,
		selectedQueueIds,
		onlyMine,
		selectedTagId,
		selectedAssignedUserId,
		selectedDateFrom,
		selectedDateTo,
		selectedWhatsappId,
		selectedTransferred,
		selectedUnreadOnly,
	]);

	useEffect(() => {
		if (typeof updateCount === "function") {
			updateCount(ticketsList.filter(belongsToCurrentView).length);
		}
	}, [
		ticketsList,
		updateCount,
		onlyMine,
		user,
		selectedQueueIds,
		showAll,
		selectedTagId,
		selectedAssignedUserId,
		selectedDateFrom,
		selectedDateTo,
		selectedWhatsappId,
		selectedTransferred,
		selectedUnreadOnly,
	]);

	const visibleTickets = ticketsList.filter(belongsToCurrentView);
	const selectableTickets = visibleTickets.filter((ticket) => ticket.status !== "closed");
	const allSelected =
		selectableTickets.length > 0 &&
		selectableTickets.every((ticket) => selectedTicketIds.includes(ticket.id));
	const selectedCount = selectedTicketIds.length;

	useEffect(() => {
		const selectableIds = new Set(selectableTickets.map((ticket) => ticket.id));
		setSelectedTicketIds((prevState) => {
			const nextState = prevState.filter((id) => selectableIds.has(id));
			const isSameLength = nextState.length === prevState.length;
			const isSameValues =
				isSameLength && nextState.every((id, index) => id === prevState[index]);

			return isSameValues ? prevState : nextState;
		});
	}, [selectableTickets, status, searchParam]);

	const handleToggleTicketSelection = (ticketId, checked) => {
		setSelectedTicketIds((prevState) => {
			if (checked) {
				if (prevState.includes(ticketId)) return prevState;
				return [...prevState, ticketId];
			}
			return prevState.filter((id) => id !== ticketId);
		});
	};

	const handleToggleSelectAll = (checked) => {
		if (!checked) {
			setSelectedTicketIds([]);
			return;
		}
		setSelectedTicketIds(selectableTickets.map((ticket) => ticket.id));
	};

	const handleBulkCloseTickets = async () => {
		if (!selectedTicketIds.length || isBulkClosing) return;
		setIsBulkClosing(true);
		let successCount = 0;
		let failCount = 0;

		for (const ticketId of selectedTicketIds) {
			try {
				await api.put(`/tickets/${ticketId}`, {
					status: "closed",
					userId: user?.id,
				});
				successCount += 1;
			} catch (error) {
				failCount += 1;
			}
		}

		setIsBulkClosing(false);
		setSelectedTicketIds([]);

		if (failCount > 0) {
			toast.warning(`Finalizados: ${successCount}. Falharam: ${failCount}.`);
			return;
		}

		toast.success(`${successCount} ticket(s) finalizado(s) com sucesso.`);
	};

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
		<Paper className={classes.ticketsListWrapper} style={style}>
			{isAdmin && selectableTickets.length > 0 && (
				<div className={classes.bulkActions}>
					<div className={classes.bulkActionsLeft}>
						<Checkbox
							color="primary"
							checked={allSelected}
							indeterminate={!allSelected && selectedCount > 0}
							onChange={(e) => handleToggleSelectAll(e.target.checked)}
						/>
						<Typography variant="body2">
							{selectedCount > 0
								? `${selectedCount} selecionado(s)`
								: "Selecionar tickets"}
						</Typography>
					</div>

					<Button
						variant="contained"
						color="primary"
						size="small"
						disabled={selectedCount === 0 || isBulkClosing}
						onClick={handleBulkCloseTickets}
					>
						{isBulkClosing ? "Finalizando..." : "Finalizar selecionados"}
					</Button>
				</div>
			)}
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{visibleTickets.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						visibleTickets.map(ticket => (
							<TicketListItem
								ticket={ticket}
								key={ticket.id}
								enableSelection={isAdmin && ticket.status !== "closed"}
								selected={selectedTicketIds.includes(ticket.id)}
								onToggleSelect={handleToggleTicketSelection}
							/>
						))
					)}

					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
		</Paper>
	);
};

export default TicketsList;
